'use client'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { 
  GraduationCap, 
  Play,
  RefreshCw,
  Sparkles 
} from 'lucide-react'

interface OnboardingStartButtonProps {
  onStart: () => void
  hasCompleted?: boolean
  variant?: 'default' | 'compact' | 'banner'
  className?: string
}

export function OnboardingStartButton({
  onStart,
  hasCompleted = false,
  variant = 'default',
  className,
}: OnboardingStartButtonProps) {
  if (variant === 'compact') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onStart}
        className={cn("gap-2", className)}
      >
        <GraduationCap className="h-4 w-4" />
        {hasCompleted ? 'Revoir le tutoriel' : 'Tutoriel'}
      </Button>
    )
  }

  if (variant === 'banner') {
    return (
      <div className={cn(
        "bg-gradient-to-r from-primary/10 via-primary/5 to-transparent",
        "border border-primary/20 rounded-lg p-4",
        "flex items-center justify-between gap-4",
        className
      )}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-primary/10">
            <GraduationCap className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h4 className="font-medium text-sm">
              {hasCompleted ? 'Revoir le tutoriel' : 'Nouveau sur VisualGrid ?'}
            </h4>
            <p className="text-xs text-muted-foreground">
              {hasCompleted 
                ? 'Rafraîchissez vos connaissances avec le tutoriel interactif'
                : 'Découvrez les fonctionnalités en quelques minutes'
              }
            </p>
          </div>
        </div>
        <Button size="sm" onClick={onStart} className="gap-2">
          {hasCompleted ? (
            <>
              <RefreshCw className="h-4 w-4" />
              Recommencer
            </>
          ) : (
            <>
              <Play className="h-4 w-4" />
              Démarrer
            </>
          )}
        </Button>
      </div>
    )
  }

  // Default variant
  return (
    <Button
      onClick={onStart}
      className={cn(
        "gap-2 bg-gradient-to-r from-primary to-primary/80",
        "hover:from-primary/90 hover:to-primary/70",
        className
      )}
    >
      <Sparkles className="h-4 w-4" />
      {hasCompleted ? 'Revoir le tutoriel' : 'Démarrer le tutoriel'}
    </Button>
  )
}
